import React, { useState, useEffect, useRef } from "react";
import { serverFetch, useGetIsPhone } from "@extensions";
import { useCompany, useUser } from "@redux";
import dayjs from "dayjs";
import { Select, Button, message, Spin } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import CatalogSale from "../catalog/catalog-sale";
import Payment from "../payment/payment";
import "./sched.css";

const getWeekStart = (date) => {
  const day = date.day();
  return date.subtract(day === 0 ? 6 : day - 1, "day").startOf("day");
};

const getWeekEnd = (date) => {
  return getWeekStart(date).add(6, "day").endOf("day");
};

const Sched = () => {
  const company = useCompany();
  const user = useUser();
  const isPhone = useGetIsPhone();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(dayjs()));
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedGroupRecord, setSelectedGroupRecord] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSales, setPaymentSales] = useState([]);

  const weekStart = getWeekStart(currentWeekStart);
  const weekEnd = getWeekEnd(currentWeekStart);
  const schedContainerRef = useRef(null);
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;

  useEffect(() => {
    const el = schedContainerRef.current;
    if (company && el) {
      if (company.catalogBackgroundImage) {
        el.style.backgroundImage = `url("${company.catalogBackgroundImage}")`;
      } else {
        el.style.backgroundImage = "";
      }
    }
    return () => {
      if (el) {
        el.style.backgroundImage = "";
      }
    };
  }, [company]);

  useEffect(() => {
    if (company) {
      fetchSchedule();
    }
  }, [company, currentWeekStart]);

  useEffect(() => {
    extractFilters();
    applyFilters();
  }, [records]);

  useEffect(() => {
    applyFilters();
  }, [selectedRoomId, selectedUserId]);

  const extractFilters = () => {
    const uniqueRooms = [];
    const uniqueUsers = [];
    const roomMap = new Map();
    const userMap = new Map();

    records.forEach((record) => {
      if (record.roomId && !roomMap.has(record.roomId)) {
        roomMap.set(record.roomId, record.roomId);
        uniqueRooms.push({
          id: record.roomId,
          name: record.roomName || `Зал ${record.roomId}`,
        });
      }
      if (record.userId && !userMap.has(record.userId)) {
        userMap.set(record.userId, record.userId);
        uniqueUsers.push({
          id: record.userId,
          name: record.userName || `Тренер ${record.userId}`,
        });
      }
    });

    setRooms(uniqueRooms);
    setUsers(uniqueUsers);
  };

  const applyFilters = () => {
    let filtered = [...records];

    if (selectedRoomId) {
      filtered = filtered.filter((r) => r.roomId === selectedRoomId);
    }

    if (selectedUserId) {
      filtered = filtered.filter((r) => r.userId === selectedUserId);
    }

    setFilteredRecords(filtered);
  };

  const fetchSchedule = async () => {
    if (!company) return;

    setLoading(true);
    try {
      const queryParams = {
        from: weekStart.format("YYYY-MM-DD"),
        to: weekEnd.format("YYYY-MM-DD"),
      };
      const data = await serverFetch("Online/Schedule", { method: "GET", queryParams: queryParams }, user);
      setRecords(data || []);
    } catch (error) {
      console.error("Ошибка получения расписания:", error);
      message.error(error?.UserMessage || "Не удалось загрузить расписание");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => {
    setCurrentWeekStart(currentWeekStart.subtract(7, "day"));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(currentWeekStart.add(7, "day"));
  };

  const handleToday = () => {
    setCurrentWeekStart(getWeekStart(dayjs()));
  };

  const getMonthName = (date) => {
    const monthNames = [
      "январь",
      "февраль",
      "март",
      "апрель",
      "май",
      "июнь",
      "июль",
      "август",
      "сентябрь",
      "октябрь",
      "ноябрь",
      "декабрь",
    ];
    return monthNames[date.month()];
  };

  const getWeekLabel = () => {
    const start = weekStart;
    const end = weekEnd;
    const startMonth = getMonthName(start);
    const endMonth = getMonthName(end);

    if (start.month() === end.month()) {
      return `${start.date()} - ${end.date()} ${startMonth}`;
    } else {
      return `${start.date()} ${startMonth} - ${end.date()} ${endMonth}`;
    }
  };

  const getWeekMonthLabel = () => {
    const start = weekStart;
    const end = weekEnd;
    const startMonth = getMonthName(start);
    const endMonth = getMonthName(end);

    if (start.month() === end.month()) {
      return startMonth;
    } else {
      return `${startMonth} - ${endMonth}`;
    }
  };

  const getDayLabel = (date) => {
    const dayNames = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
    const dayOfWeek = date.day();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    return `${dayNames[isoDayOfWeek - 1]} ${date.format("DD.MM")}`;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const newWeekStart = getWeekStart(date);
    setCurrentWeekStart(newWeekStart);
  };

  const getTimeSlots = () => {
    const slots = [];
    if (!company) {
      for (let hour = 7; hour <= 19; hour++) {
        slots.push(hour);
      }
      return slots;
    }

    const startHour = company.hourStart !== undefined && company.hourStart !== null ? company.hourStart : 7;
    let endHour = company.hourFinish !== undefined && company.hourFinish !== null ? company.hourFinish : 19;

    if (endHour === 23) {
      endHour = 22;
    } else if (endHour === 0) {
      endHour = 23;
    }

    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(hour);
    }
    return slots;
  };

  const getRecordsForDayAndTime = (date, hour) => {
    return filteredRecords.filter((record) => {
      if (!record.docDate) return false;
      const recordDate = dayjs(record.docDate);
      if (!recordDate.isSame(date, "day")) return false;

      if (record.start) {
        let startStr = record.start;
        if (typeof startStr === "string") {
          const parts = startStr.split(":");
          if (parts.length > 0) {
            const startHour = parseInt(parts[0]);
            return startHour === hour;
          }
        }
      }
      return false;
    });
  };

  const formatTime = (timeSpan) => {
    if (!timeSpan) return "";
    if (typeof timeSpan === "string") {
      return timeSpan.length >= 5 ? timeSpan.substring(0, 5) : timeSpan;
    }
    return timeSpan;
  };

  const getProductName = (record) => {
    if (record.cnts && record.cnts.length > 0) {
      const productCnt = record.cnts.find((cnt) => cnt.objectVid === 2);
      if (productCnt && productCnt.objectName) {
        return productCnt.objectName;
      }
    }
    return record.comment || "Занятие";
  };

  const getRecordClientCount = (record) => {
    if (record.clientCount !== undefined && record.clientCount !== null) {
      return record.clientCount;
    }
    if (record.cnts && record.cnts.length > 0) {
      return record.cnts
        .filter((cnt) => cnt.objectVid === 1)
        .reduce((sum, cnt) => sum + Math.max(cnt.qty || 1, 1), 0);
    }
    return 0;
  };

  const canBookGroupRecord = (record) => {
    if (record.canBookOnline !== undefined && record.canBookOnline !== null) {
      return record.canBookOnline;
    }
    return Boolean(
      record.isGroup
      && record.bookOnline
      && record.productId
      && record.catalogItemId
      && record.maxClients > 0
      && getRecordClientCount(record) < record.maxClients
    );
  };

  const getCatalogSaleItem = (record) => ({
    id: record.catalogItemId,
    catalogId: record.catalogItemId,
    productId: record.productId,
    name: record.catalogName || record.productName || getProductName(record),
    description: record.description || "",
    price: record.catalogPrice || 0,
  });

  const handleGroupRecordOpen = (record) => {
    if (!canBookGroupRecord(record)) {
      return;
    }
    setSelectedGroupRecord(record);
  };

  const handleGroupRecordKeyDown = (event, record) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    handleGroupRecordOpen(record);
  };

  const handleSaleClose = () => {
    setSelectedGroupRecord(null);
  };

  const getDaysOfWeek = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(weekStart.add(i, "day"));
    }
    return days;
  };

  const timeSlots = getTimeSlots();
  const days = getDaysOfWeek();

  useEffect(() => {
    if (isPhone && days.length > 0 && !selectedDate) {
      const today = dayjs().startOf("day");
      const todayInWeek = days.find((d) => d.isSame(today, "day"));
      setSelectedDate(todayInWeek ?? days[0]);
    } else if (!isPhone) {
      setSelectedDate(null);
    }
  }, [isPhone, days]);

  return (
    <div className="sched-container" ref={schedContainerRef} style={backgroundStyle}>
      <div className="sched-header">
        {!isPhone && (
          <div className="sched-header-left">
            <Button onClick={handleToday} className="sched-today-btn">
              Сегодня
            </Button>
            <div className="sched-date-range">
              <Button className="sched-nav-btn" onClick={handlePrevWeek}>
                ‹
              </Button>
              <span className="sched-date-label">{getWeekLabel()}</span>
              <Button className="sched-nav-btn" onClick={handleNextWeek}>
                ›
              </Button>
            </div>
          </div>
        )}
        {isPhone && (
          <div className="sched-mobile-dates-wrapper">
            <div className="sched-mobile-month-label">{getWeekMonthLabel()}</div>
            <div className="sched-mobile-dates">
              <button
                className="sched-mobile-nav"
                onClick={handlePrevWeek}
              >
                ‹
              </button>
              <div className="sched-mobile-dates-list">
              {days.map((day, index) => {
                const dayOfWeek = day.day();
                const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
                const isWeekend = isoDayOfWeek === 6 || isoDayOfWeek === 7;
                const isSelected = selectedDate && selectedDate.isSame(day, "day");
                return (
                  <button
                    key={index}
                    className={`sched-mobile-date ${isSelected ? "selected" : ""} ${isWeekend ? "weekend" : ""}`}
                    onClick={() => handleDateSelect(day)}
                  >
                    <div className="sched-mobile-date-name">
                      {getDayLabel(day).split(" ")[0]}
                    </div>
                    <div className="sched-mobile-date-number">
                      {day.date()}
                    </div>
                  </button>
                );
              })}
            </div>
              <button
                className="sched-mobile-nav"
                onClick={handleNextWeek}
              >
                ›
              </button>
            </div>
          </div>
        )}
        <div className="sched-header-right">
          <Select
            placeholder="Зал"
            allowClear
            value={selectedRoomId}
            onChange={setSelectedRoomId}
            className="sched-filter"
            options={rooms.map((r) => ({ label: r.name, value: r.id }))}
          />
          <Select
            placeholder="Тренер"
            allowClear
            value={selectedUserId}
            onChange={setSelectedUserId}
            className="sched-filter"
            options={users.map((u) => ({ label: u.name, value: u.id }))}
          />
        </div>
      </div>

      {loading ? (
        <div className="sched-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="sched-grid">
            {!isPhone && (
              <div className="sched-grid-header-row">
                <div className="sched-time-header" aria-hidden />
                <div className="sched-days-header-row">
                  {days.map((day, index) => (
                    <div key={index} className="sched-day-header">
                      {getDayLabel(day)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="sched-grid-body">
              {timeSlots.map((hour, hourIndex) => {
                const checkDays = isPhone && selectedDate ? [selectedDate] : days;
                const hasRecordsForRow = checkDays.some(
                  (day) => getRecordsForDayAndTime(day, hour).length > 0
                );
                const rowZ = timeSlots.length - hourIndex;

                const renderCell = (day) => {
                  const dayRecords = getRecordsForDayAndTime(day, hour);
                  const rowHasAnyDesktop = days.some(
                    (d) => getRecordsForDayAndTime(d, hour).length > 0
                  );
                  const useCompactEmpty = isPhone
                    ? dayRecords.length === 0
                    : !rowHasAnyDesktop;
                  return (
                    <div
                      key={day.valueOf()}
                      className={`sched-cell ${useCompactEmpty ? "sched-cell-empty" : ""}`}
                    >
                      {dayRecords.map((record, recordIndex) => {
                        const bookable = canBookGroupRecord(record);
                        return (
                          <div
                            key={recordIndex}
                            className={`sched-record ${bookable ? "sched-record--bookable" : ""}`}
                            onClick={() => handleGroupRecordOpen(record)}
                            onKeyDown={(event) => handleGroupRecordKeyDown(event, record)}
                            role={bookable ? "button" : undefined}
                            tabIndex={bookable ? 0 : undefined}
                          >
                            {bookable && (
                              <button
                                type="button"
                                className="sched-record-book-button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleGroupRecordOpen(record);
                                }}
                                aria-label="Записаться"
                                title="Записаться"
                              >
                                <ShoppingCartOutlined />
                              </button>
                            )}
                            <div className="sched-record-name">{getProductName(record)}</div>
                            <div className="sched-record-time">
                              {formatTime(record.start)} - {formatTime(record.finish)}
                            </div>
                            <div className="sched-record-details">
                              {record.roomName && (
                                <span className="sched-record-room">{record.roomName}</span>
                              )}
                              {record.userName && (
                                <span className="sched-record-trainer">{record.userName}</span>
                              )}
                               {record.comment && (
                                <span className="sched-record-comment">{record.comment}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                };

                return (
                  <div key={hour} className="sched-row" style={{ zIndex: rowZ }}>
                    <div
                      className={`sched-time-cell ${!hasRecordsForRow ? "sched-time-cell-empty" : ""}`}
                    >
                      {hour}:00
                    </div>
                    <div
                      className={`sched-row-days ${isPhone ? "sched-row-days--phone" : ""}`}
                    >
                      {isPhone && selectedDate
                        ? renderCell(selectedDate)
                        : !isPhone
                          ? days.map((day) => renderCell(day))
                          : (
                              <div className="sched-cell sched-cell-empty" />
                            )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {selectedGroupRecord && (
        <CatalogSale
          item={getCatalogSaleItem(selectedGroupRecord)}
          open={true}
          onClose={handleSaleClose}
          initialGroupRecord={selectedGroupRecord}
          onPaymentOpen={(sales) => {
            setPaymentSales(sales);
            setPaymentOpen(true);
          }}
        />
      )}
      <Payment
        sales={paymentSales}
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
      />
    </div>
  );
};

export default Sched;

